import React, { useEffect, useState } from "react";
import {
  Box,
  Image,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Heading,
  Divider,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { db, storage } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import LottieLoader from "../Animations/LottieLoader";
import { getDownloadURL, ref } from "firebase/storage";

const IMAGES_PER_PAGE = 10;

const PromptGallery = () => {
  const [categories, setCategories] = useState([]); // [{ ...category, images: [...], visibleCount: 10 }]
  const [isLoading, setIsLoading] = useState(true);

  // Modal for full image (optional, as in your old code)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const color = useColorModeValue("gray.300", "gray.600");

  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, "promptGallery"));
        const imagesData = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const data = doc.data();
            // Fetch URLs for each image path
            const generatedImagesUrls = await Promise.all(
              data.images.map(async (imagePath) => {
                const imageRef = ref(storage, imagePath);
                const imageUrl = await getDownloadURL(imageRef);
                return imageUrl;
              })
            );
            return {
              ...data,
              images: generatedImagesUrls,
              visibleCount: IMAGES_PER_PAGE, // Start with first N images shown
            };
          })
        );
        setCategories(imagesData);
      } catch (error) {
        console.error("Error fetching images:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchImages();
  }, []);

  // Handler for loading more images in a category
  const handleSeeMore = (catIdx) => {
    setCategories((prev) =>
      prev.map((cat, idx) =>
        idx === catIdx
          ? { ...cat, visibleCount: cat.visibleCount + IMAGES_PER_PAGE }
          : cat
      )
    );
  };

  // Handler for image click (optional modal view)
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    onOpen();
  };

  return (
    <Box>
      <Box>
        <Heading
          as="h1"
          size={{ base: "md", md: "xl" }}
          color={textColor}
          mb={2}
        >
          Prompt Engineering Showcase
        </Heading>
        <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
          A collection of my personal generated AI images through various AI
          image generation platforms like Midjourney, Flux AI, and others
        </Text>
        <Divider my={"4"} color={color} />
      </Box>
      {isLoading ? (
        <LottieLoader />
      ) : (
        categories.map((category, index) => (
          <Box key={index} mb={8}>
            {category.title && (
              <Heading as="h3" size="md" mb={3} color={textColor}>
                {category.title}
              </Heading>
            )}
            <Grid
              templateColumns="repeat(auto-fill, minmax(150px, 1fr))"
              autoRows="150px"
              gap={4}
            >
              {category.images
                .slice(0, category.visibleCount)
                .map((imageUrl, idx) => {
                  // Optional: bento effect as before
                  const rowSpan = idx % 5 === 0 ? 2 : 1;
                  const colSpan = idx % 3 === 0 ? 2 : 1;

                  return (
                    <GridItem
                      key={`${index}-${idx}`}
                      rowSpan={rowSpan}
                      colSpan={colSpan}
                      overflow="hidden"
                    >
                      <Box
                        as="button"
                        width="100%"
                        height="100%"
                        onClick={() => handleImageClick(imageUrl)}
                      >
                        <Image
                          src={imageUrl}
                          objectFit="cover"
                          borderRadius={"md"}
                          width="100%"
                          height="100%"
                          transition="transform 0.2s"
                          _hover={{ transform: "scale(1.05)" }}
                        />
                      </Box>
                    </GridItem>
                  );
                })}
            </Grid>
            {/* See More Button */}
            {category.visibleCount < category.images.length && (
              <Button
                onClick={() => handleSeeMore(index)}
                mt={4}
                colorScheme="teal"
                size="sm"
              >
                See More
              </Button>
            )}
          </Box>
        ))
      )}

      {/* Optional: Modal for full image view */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={bgColor}>
          <ModalHeader>Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Image src={selectedImage} maxH="70vh" maxW="100%" borderRadius="md" />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PromptGallery;
